/**
 * AppContainer Component
 * Main wrapper with consistent padding and max-width
 * Now display mode aware for standalone vs embedded layouts
 */

import type { ReactNode } from 'react';
import { designTokens } from '../../constants/designTokens';
import { useDisplayModeContext } from '../../hooks/useDisplayModeContext';

interface AppContainerProps {
  children: ReactNode;
  maxWidth?:
    | 'narrow'
    | 'content'
    | 'wide'
    | 'desktop'
    | 'desktopWide'
    | 'desktopXWide'
    | 'container';
  centered?: boolean;
  className?: string;
}

export function AppContainer({
  children,
  maxWidth = 'content',
  centered = true,
  className = '',
}: AppContainerProps) {
  const { mode } = useDisplayModeContext();
  const containerClass = `app-container ${centered ? 'centered' : ''} ${className}`;
  const embeddedBaseMaxWidth =
    maxWidth === 'narrow'
      ? designTokens.layout.maxWidth.narrow
      : maxWidth === 'wide'
        ? designTokens.layout.maxWidth.wide
        : designTokens.layout.maxWidth.content;
  const desktopMaxWidth =
    maxWidth === 'narrow'
      ? designTokens.layout.maxWidth.desktop
      : maxWidth === 'wide' || maxWidth === 'desktopWide'
        ? designTokens.layout.maxWidth.desktopWide
        : maxWidth === 'desktopXWide'
          ? designTokens.layout.maxWidth.desktopXWide
          : maxWidth === 'container'
            ? designTokens.layout.maxWidth.container
            : designTokens.layout.maxWidth.desktop;

  return (
    <div className={containerClass} data-display-mode={mode}>
      {children}

      <style>{`
        .app-container {
          width: 100%;
          margin: 0 auto;
          
          /* Clean, flat design - no visual boundaries */
          min-height: 0;
          box-sizing: border-box;
          /* Remove any potential borders, shadows, or background effects */
          border: none;
          background: transparent;
          box-shadow: none;
        }
        
        /* Embedded mode - Apply max-width constraints */
        .app-container[data-display-mode="embedded"] {
          /* Mobile-First: Start with mobile max-width and padding */
          max-width: ${embeddedBaseMaxWidth};
          padding: ${designTokens.layout.containerPadding.mobile};
          justify-content: flex-start;
        }
        
        .app-container.centered {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
        }
        
        /* Standalone mode - Enhanced desktop container utilization */
        .app-container[data-display-mode="standalone"] {
          max-width: none;
          width: 100%;
          height: 100%;
        }
        
        /* Small mobile devices */
        ${designTokens.media.mobile} {
          .app-container[data-display-mode="embedded"] {
            padding: ${designTokens.spacing.sm};
            max-width: 100%;
          }
          .app-container[data-display-mode="standalone"] {
            padding: ${designTokens.spacing.sm};
          }
        }
        
        /* Tablet (768px-1023px) */
        ${designTokens.media.tablet} {
          .app-container {
            padding: ${designTokens.layout.containerPadding.tablet};
          }
          .app-container[data-display-mode="embedded"] {
            padding: ${designTokens.layout.containerPadding.tablet};
          }
        }
        
        /* Desktop (1024px+) */
        ${designTokens.media.desktop} {
          .app-container {
            padding: ${designTokens.layout.containerPadding.desktop};
          }
          .app-container[data-display-mode="embedded"] {
            padding: ${designTokens.layout.containerPadding.desktop};
            max-width: ${desktopMaxWidth};
          }
          .app-container[data-display-mode="standalone"] {
            max-width: ${desktopMaxWidth};
            margin: 0 auto;
          }
        }
        
        /* Mobile landscape optimizations */
        @media (max-height: 500px) and (orientation: landscape) {
          .app-container {
            padding: ${designTokens.spacing.sm} ${designTokens.spacing.lg};
          }
          
          .app-container.centered {
            min-height: 100vh;
            justify-content: flex-start;
            padding-top: ${designTokens.spacing.md};
          }
        }
      `}</style>
    </div>
  );
}
